import { getAuthHeader } from './authApi';

// Services API utility
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? '/api/v1' : (process.env.REACT_APP_API_URL || '/api/v1');

export interface Service {
  id: number;
  title: string;
  description: string;
  iconType: 'EMOJI' | 'FONT_AWESOME' | 'MATERIAL_ICON' | 'SVG' | 'IMAGE_URL';
  iconValue: string;
  displayOrder: number;
  isActive: boolean;
}

export const servicesApi = {
  // Get all active services (Public)
  async getAllServices(): Promise<Service[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/services`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching services:', error);
      // Return fallback data if API fails
      return [
        {
          id: 1,
          title: "Full-Stack Development",
          description: "Complete web application development from database design to frontend implementation.",
          iconType: 'EMOJI',
          iconValue: "🚀",
          displayOrder: 1,
          isActive: true
        },
        {
          id: 2,
          title: "API Development",
          description: "RESTful API design and development with proper authentication, documentation, and testing.",
          iconType: 'EMOJI',
          iconValue: "⚡",
          displayOrder: 2,
          isActive: true
        },
        {
          id: 3,
          title: "System Architecture",
          description: "Microservices architecture, containerization with Docker, CI/CD pipelines, and cloud deployment strategies.",
          iconType: 'EMOJI',
          iconValue: "🔧",
          displayOrder: 3,
          isActive: true
        },
        {
          id: 4,
          title: "Technical Consulting",
          description: "Technology strategy, code reviews, performance optimization, and technical mentoring.",
          iconType: 'EMOJI',
          iconValue: "💡",
          displayOrder: 4,
          isActive: true
        },
        {
          id: 5,
          title: "Android Mobile Development",
          description: "Native Android application development using Kotlin and Java. Modern UI/UX design, performance optimization, and Google Play Store deployment.",
          iconType: 'EMOJI',
          iconValue: "📱",
          displayOrder: 5,
          isActive: true
        }
      ];
    }
  },

  // Get all services including inactive (Admin)
  async getAllServicesAdmin(): Promise<Service[]> {
    const response = await fetch(`${API_BASE_URL}/services/all`, {
      headers: getAuthHeader()
    });
    if (!response.ok) throw new Error('Failed to fetch all services');
    return response.json();
  },

  async addService(service: Omit<Service, 'id'>): Promise<Service> {
    const response = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(service),
    });
    if (!response.ok) throw new Error('Failed to add service');
    return response.json();
  },

  async updateService(id: number, service: Partial<Service>): Promise<Service> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(service),
    });
    if (!response.ok) throw new Error('Failed to update service');
    return response.json();
  },

  async deleteService(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    if (!response.ok) throw new Error('Failed to delete service');
  }
};